import { useState, useEffect, FC, useMemo } from 'react';
import { Button, ButtonGroup, Checkbox } from 'rsuite';
import { searchMatch } from 'containers/RtbCampaigns/RtbCampaignSinglePage/Components/Keyword/ManualKeyword/utils';
import SearchIcon from '@rsuite/icons/Search';
import useCreativeStore from 'store/creativeStore';
import CreativeCard from './CreativeCard/CreativeCard';
import useCreativeForm from 'components/Rsuite/CreativeForm/Hooks/useCreativeForm';
import { CreativeOfCampaign } from 'core/creative/Creative';
import { RsInput, RsInputSize } from '../Input';
import moment from 'moment';
import { TablePlaceholder } from 'components/TablePlaceholder/TablePlaceholder';
import { useCampaignStore } from 'store/campaignStore';
import { AdType } from 'core/rtbCampaign/RtbCampaign';

interface OptionsProps {
  value: number;
  label: string;
}

interface BindCreativeFormProps {
  campaignId: string;
}

const BindCreativeForm: FC<BindCreativeFormProps> = ({ campaignId }) => {
  const { bindCreatives, handleAddBindCreatives } = useCreativeStore();
  const { adType, brand } = useCampaignStore();
  const { getWaitingBindCreatives } = useCreativeForm();
  const [defaultCreatives, setDefaultCreatives] = useState<
    CreativeOfCampaign[]
  >([]);
  const [creativeTypeOptions, setCreativeTypeOptions] = useState<
    OptionsProps[]
  >([]);
  const sortOptions = [
    { value: 0, label: '新到舊' },
    { value: 1, label: '舊到新' },
  ];
  const [filterType, setFilterType] = useState({ value: 0, label: '已選取' });
  const [sortType, setSortType] = useState({ value: 0, label: '新到舊' });
  const [searchValue, setSearchValue] = useState('');

  const filteredData = useMemo(
    () => defaultCreatives.filter(({ name }) => searchMatch(searchValue, name)),
    [defaultCreatives, searchValue],
  );
  const handleSortData = () => {
    return filteredData.sort((a, b) => {
      if (sortType.value === 0) {
        return moment(b.createTime).diff(moment(a.createTime));
      } else {
        return moment(a.createTime).diff(moment(b.createTime));
      }
    });
  };
  const handleFilterByType = (type: number) => {
    if (type === 0) {
      return handleSortData().filter((creative) =>
        bindCreatives.includes(creative.id.toString()),
      );
    }
    if (type === 14) {
      return handleSortData().filter(
        (creative) =>
          creative.creativeValue?.webBrandNo === brand.value &&
          creative.creativeType === type,
      );
    }
    return handleSortData().filter(
      (creative) => creative.creativeType === type,
    );
  };

  const handleCheckAll = (value, checked) => {
    handleAddBindCreatives(
      checked
        ? [...defaultCreatives.map((creative) => creative.id.toString())]
        : [],
    );
  };

  const handleCheck = (value, checked) => {
    checked
      ? !bindCreatives.includes(value) &&
        handleAddBindCreatives([...bindCreatives, value])
      : handleAddBindCreatives(
          bindCreatives.filter((item) => item.toString() !== value),
        );
  };

  const handleShopCheck = (value, checked) => {
    checked
      ? !bindCreatives.includes(value) && handleAddBindCreatives([...[], value])
      : handleAddBindCreatives(
          bindCreatives.filter((item) => item.toString() !== value),
        );
  };

  const handleHasNonBindCreative = (defaultCreatives, creativeType: number) => {
    if (creativeType === 12) {
      return defaultCreatives.some(
        (creative) => creative?.creativeValue?.images?.[0]?.w === 960,
      );
    } else {
      return defaultCreatives.some(
        (creative) => creative.creativeType === creativeType,
      );
    }
  };

  useEffect(() => {
    getWaitingBindCreatives(adType).then((defaultCreatives) => {
      if (defaultCreatives) {
        const defaultCreativeTypeOptions = () => {
          switch (adType) {
            case AdType.DISPLAY:
              return [
                { value: 1, label: '圖像式素材' },
                { value: 9, label: '原生商品素材' },
                { value: 12, label: '圖像加商品素材' },
                { value: 0, label: '已選取' },
              ];
            case AdType.SHOP:
              return [
                { value: 14, label: '黃金置頂素材' },
                { value: 0, label: '已選取' },
              ];
            default:
              return [{ value: 0, label: '已選取' }];
          }
        };
        setCreativeTypeOptions(
          defaultCreativeTypeOptions().filter((option) => {
            return (
              handleHasNonBindCreative(defaultCreatives, option.value) ||
              option.value === 0
            );
          }),
        );
        setDefaultCreatives(
          defaultCreatives.filter((creative) => {
            if (creative.creativeType === 12) {
              return creative?.creativeValue?.images?.[0]?.w === 960;
            }
            return true;
          }),
        );
      }
    });
  }, []);

  useEffect(() => {
    setFilterType(creativeTypeOptions[0]);
  }, [creativeTypeOptions]);

  return (
    <div>
      <div className='mb-[20px] px-8 pt-14'>
        <div className='mb-[20px] flex items-center justify-start gap-4'>
          <RsInput
            value={searchValue}
            onChange={setSearchValue}
            size={RsInputSize.MEDIUM}
            frontAddon={<SearchIcon />}
            placeholder='請輸入關鍵字'
          />
          {sortType && (
            <ButtonGroup>
              {sortOptions.map((option) => (
                <Button
                  key={`sort-${option.value}`}
                  appearance={
                    option.value === sortType.value ? 'primary' : 'default'
                  }
                  active={option.value === sortType.value}
                  onClick={() => setSortType(option)}
                >
                  {option.label}
                </Button>
              ))}
            </ButtonGroup>
          )}
        </div>
        <ButtonGroup className='mb-[20px]' size='md'>
          {filterType &&
            creativeTypeOptions.map((option) => (
              <Button
                key={`creativeType-${option.value}`}
                appearance={
                  option.value === filterType.value ? 'primary' : 'default'
                }
                active={option.value === filterType.value}
                onClick={() => setFilterType(option)}
              >
                {option.label}
              </Button>
            ))}
        </ButtonGroup>
        {adType !== AdType.SHOP && (
          <div>
            <Checkbox
              indeterminate={
                bindCreatives.length > 0 &&
                bindCreatives.length < defaultCreatives.length
              }
              onChange={handleCheckAll}
              checked={bindCreatives.length === defaultCreatives.length}
            >
              全選
            </Checkbox>
          </div>
        )}
      </div>
      {defaultCreatives.length > 0 && (
        <div className='grid auto-rows-auto grid-cols-3 gap-4 px-8 pb-8'>
          {filterType &&
            handleFilterByType(filterType.value).map((creative) => {
              return (
                <CreativeCard
                  key={creative.id}
                  creative={creative}
                  checked={bindCreatives.includes(creative?.id.toString())}
                  onCheck={
                    adType === AdType.SHOP ? handleShopCheck : handleCheck
                  }
                />
              );
            })}
        </div>
      )}
      {defaultCreatives.length === 0 && (
        <TablePlaceholder placeholder={'無素材'} />
      )}
    </div>
  );
};

export default BindCreativeForm;
